// src/context/NotificationContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import {
  AppNotification,
  getNotifications,
  getUnreadCount,
  createNotification as apiCreateNotification,
  markAsRead as apiMarkAsRead,
  markAllAsRead as apiMarkAllAsRead,
  dismissNotification as apiDismiss,
  dismissAllNotifications as apiDismissAll,
  CreateNotificationPayload,
} from "@/api/notifications";
import { applyCacheWatermarks } from "@/utils/dashboardCache";

const POLL_INTERVAL_MS = 30_000;

export type NotificationContextType = {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  addNotification: (data: CreateNotificationPayload) => Promise<AppNotification | null>;
  markAsRead: (publicId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (publicId: string) => Promise<void>;
  dismissAll: () => Promise<void>;
  refetch: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user, activeAccount } = useAuth();
  const accountId = activeAccount?.id;
  const isAuthenticated = !!user && !!accountId;

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch unread count (lightweight, for polling). The same payload
  // carries `cache_watermarks` (data_source public_id → ISO timestamp of
  // last_cache_refill_at) — when one advances, wipe the matching
  // mainCache/compCache entries so the next dashboard load pulls fresh
  // data. See plan
  // docs/plans-pending/2026-05-19-server-side-dashboard-cache-refill.md
  // Step 11.
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const resp = await getUnreadCount();
      setUnreadCount(resp.count);
      if (resp.cache_watermarks) {
        applyCacheWatermarks(resp.cache_watermarks);
      }
    } catch {
      // Silently fail — polling will retry
    }
  }, [isAuthenticated]);

  // Fetch full notification list (for panel)
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const { items } = await getNotifications(50, 0);
      setNotifications(items);
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Combined refetch
  const refetch = useCallback(async () => {
    await Promise.all([fetchUnreadCount(), fetchNotifications()]);
  }, [fetchUnreadCount, fetchNotifications]);

  // Create notification via API
  const addNotification = useCallback(
    async (data: CreateNotificationPayload): Promise<AppNotification | null> => {
      if (!isAuthenticated) return null;
      try {
        const notification = await apiCreateNotification(data);
        await refetch();
        return notification;
      } catch {
        return null;
      }
    },
    [isAuthenticated, refetch]
  );

  // Mark as read (optimistic update)
  const markAsRead = useCallback(
    async (publicId: string) => {
      try {
        await apiMarkAsRead(publicId);
        setNotifications((prev) =>
          prev.map((n) =>
            n.public_id === publicId ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // Silently fail
      }
    },
    []
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await apiMarkAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  // Dismiss single
  const dismiss = useCallback(
    async (publicId: string) => {
      try {
        await apiDismiss(publicId);
        setNotifications((prev) =>
          prev.filter((n) => n.public_id !== publicId)
        );
        await fetchUnreadCount();
      } catch {
        // Silently fail
      }
    },
    [fetchUnreadCount]
  );

  // Dismiss all
  const dismissAll = useCallback(async () => {
    try {
      await apiDismissAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  // Reset state on account switch
  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    if (isAuthenticated) {
      fetchUnreadCount();
    }
  }, [accountId, isAuthenticated, fetchUnreadCount]);

  // Poll unread count every 30s, pause when tab hidden
  useEffect(() => {
    if (!isAuthenticated) return;

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchUnreadCount();
        startPolling();
      }
    };

    fetchUnreadCount();
    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        addNotification,
        markAsRead,
        markAllAsRead,
        dismiss,
        dismissAll,
        refetch,
        fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }
  return context;
}
